import React, { useState, useRef, useEffect } from "react";

// Deterministic waveform pattern mimicking speech audio amplitude
const WAVEFORM_HEIGHTS = [
  8, 14, 22, 10, 16, 26, 20, 12,
  18, 24, 28, 14, 18, 22, 10, 16,
  26, 20, 12, 18, 24, 14, 10, 16,
  22, 18, 12, 20, 16, 10, 14, 8
];

function formatTime(seconds) {
  if (!seconds || isNaN(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s < 10 ? "0" : ""}${s}`;
}

export default function WhatsAppVoicePlayer({
  src,
  senderName = "Customer",
  senderAvatar = null,
  isOutbound = false,
}) {
  const audioRef = useRef(null);
  const trackRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);

  // Reload audio element whenever src changes or mounts
  useEffect(() => {
    if (audioRef.current && src) {
      setIsPlaying(false);
      setCurrentTime(0);
      try {
        audioRef.current.load();
      } catch (err) {
        console.warn("[VoicePlayer] Load warning:", err);
      }
    }
  }, [src]);

  // Initial letter for fallback avatar
  const initial = (senderName || "U").trim().charAt(0).toUpperCase();

  // Play / Pause toggle
  const togglePlay = (e) => {
    e.stopPropagation();
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current
        .play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch((err) => {
          console.warn("[VoicePlayer] Playback error:", err);
          setIsPlaying(false);
        });
    }
  };

  // Speed toggle (1x -> 1.5x -> 2x -> 1x)
  const toggleSpeed = (e) => {
    e.stopPropagation();
    const nextSpeed = speed === 1 ? 1.5 : speed === 1.5 ? 2 : 1;
    setSpeed(nextSpeed);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextSpeed;
    }
  };

  // Seek by clicking waveform track
  const handleTrackClick = (e) => {
    e.stopPropagation();
    if (!audioRef.current || !trackRef.current || !duration) return;
    const rect = trackRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = ratio * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Synchronize audio events
  const onLoadedMetadata = () => {
    if (audioRef.current) {
      const d = audioRef.current.duration;
      if (d && !isNaN(d) && isFinite(d)) {
        setDuration(d);
      }
    }
  };

  const onTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      if (!duration && audioRef.current.duration) {
        setDuration(audioRef.current.duration);
      }
    }
  };

  const onEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) audioRef.current.currentTime = 0;
  };

  // Calculate progress ratio
  const progressRatio = duration > 0 ? Math.min(1, currentTime / duration) : 0;
  const activeBarIndex = Math.floor(progressRatio * WAVEFORM_HEIGHTS.length);

  return (
    <div className={`wa-voice-card ${isOutbound ? "outbound" : "inbound"}`}>
      {/* Hidden HTML5 Audio Element */}
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={onLoadedMetadata}
        onTimeUpdate={onTimeUpdate}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={onEnded}
        onError={(e) => {
          console.warn("[VoicePlayer] Audio error for source:", src, e);
          setIsPlaying(false);
        }}
      />

      {/* 1. Left Avatar with Pinned Microphone Badge */}
      <div className="wa-voice-avatar-wrap">
        {senderAvatar ? (
          <img
            src={senderAvatar}
            alt={senderName}
            className="wa-voice-avatar-img"
          />
        ) : (
          <div className="wa-voice-avatar-placeholder">
            <span>{initial}</span>
          </div>
        )}
        {/* Pinned green microphone badge */}
        <span className="wa-voice-mic-badge" title="Voice message">
          <i className="bi bi-mic-fill"></i>
        </span>
      </div>

      {/* 2. Play / Pause Control Button */}
      <button
        type="button"
        className={`wa-voice-play-btn ${isPlaying ? "playing" : ""}`}
        onClick={togglePlay}
        aria-label={isPlaying ? "Pause voice note" : "Play voice note"}
        title={isPlaying ? "Pause" : "Play"}
      >
        {isPlaying ? (
          <i className="bi bi-pause-fill"></i>
        ) : (
          <i className="bi bi-play-fill"></i>
        )}
      </button>

      {/* 3. Waveform Scrubber & Metadata Area */}
      <div className="wa-voice-content">
        {/* Interactive Waveform Track */}
        <div
          ref={trackRef}
          className="wa-voice-waveform-track"
          onClick={handleTrackClick}
          title="Click to seek"
          role="slider"
          aria-valuenow={Math.round(currentTime)}
          aria-valuemin={0}
          aria-valuemax={Math.round(duration)}
        >
          {/* Scrubber Ball */}
          <div
            className="wa-voice-scrubber-dot"
            style={{ left: `${progressRatio * 100}%` }}
          />

          {/* Vertical Bars */}
          <div className="wa-voice-bars">
            {WAVEFORM_HEIGHTS.map((height, i) => {
              const isActive = i <= activeBarIndex;
              return (
                <span
                  key={i}
                  className={`wa-voice-bar ${isActive ? "active" : ""}`}
                  style={{ height: `${height}px` }}
                />
              );
            })}
          </div>
        </div>

        {/* Bottom Details Row: Current Time & Speed Toggle */}
        <div className="wa-voice-footer">
          <span className="wa-voice-timer">
            {isPlaying
              ? formatTime(currentTime)
              : duration > 0
              ? formatTime(duration)
              : "0:00"}
          </span>

          {/* Speed Toggle Pill (WhatsApp Web 1x / 1.5x / 2x) */}
          <button
            type="button"
            className={`wa-voice-speed-pill ${speed > 1 ? "boosted" : ""}`}
            onClick={toggleSpeed}
            title="Change playback speed"
          >
            {speed}x
          </button>
        </div>
      </div>
    </div>
  );
}
